import { Divider } from "@mui/material";
import dayjs from "dayjs";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { CollapsibleCard } from "@/components/CollapsibleCard/CollapsibleCard";
import {
  DetailsList,
  DetailsListItem,
} from "@/features/trips/components/DetailsList";
import { RoomList } from "@/features/trips/components/RoomList";
import type { BookingData, TripDetails } from "@/features/trips/types/types";

type Props = {
  trip: TripDetails;
};

export function TripDetailsCard({ trip }: Props) {
  const intl = useIntl();
  const tripDate = useWatch<BookingData, "tripDate">({ name: "tripDate" });
  const travellers = useWatch<BookingData, "travellers">({
    name: "travellers",
  });

  return (
    <CollapsibleCard
      title={intl.formatMessage({
        id: "trips.tripDetails.title",
        defaultMessage: "Trip details",
      })}
      collapsible={false}
    >
      <DetailsList>
        <DetailsListItem
          title={intl.formatMessage({
            id: "trips.travelStartDate",
            defaultMessage: "Travel start date",
          })}
          value={dayjs(tripDate).format("D MMMM, YYYY")}
        />

        <DetailsListItem
          title={intl.formatMessage({
            id: "trips.travelEndDate",
            defaultMessage: "Travel end date",
          })}
          value={dayjs(tripDate)
            .add(trip.durationInDays, "day")
            .format("D MMMM, YYYY")}
        />

        <DetailsListItem
          title={intl.formatMessage({
            id: "trips.travelLength",
            defaultMessage: "Travel length",
          })}
          value={intl.formatMessage(
            {
              id: "trips.duration",
              defaultMessage:
                "{nights} {nights, plural, one {night} other {nights}} / {days} {days, plural, one {day} other {days}}",
            },
            {
              nights: trip.durationInDays - 1,
              days: trip.durationInDays,
            },
          )}
        />

        <DetailsListItem
          title={intl.formatMessage({
            id: "trips.flightOperator",
            defaultMessage: "Flight operator",
          })}
          value={trip.airline}
        />
      </DetailsList>

      <Divider
        sx={{
          borderColor: "var(--color-stroke-neutral-soft)",
          my: "calc(var(--spacing)*3)",
        }}
      />

      <div className="mb-1 text-sm text-text-disabled-primary">
        {intl.formatMessage({
          id: "trips.tripDetails.rooms",
          defaultMessage: "Booked rooms",
        })}
      </div>
      <RoomList rooms={travellers} />
    </CollapsibleCard>
  );
}
